import { useState, useEffect } from "react";

export interface RouteState {
  path: string;
  section: string;
  hash: string;
}

export function scrollToElement(elementId: string) {
  if (!elementId || typeof window === "undefined") return;
  const cleanId = elementId.replace(/^#/, "");

  const attemptScroll = (attemptsLeft = 8) => {
    const el = document.getElementById(cleanId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (attemptsLeft > 0) {
      setTimeout(() => attemptScroll(attemptsLeft - 1), 60);
    }
  };

  requestAnimationFrame(() => attemptScroll());
}

export function navigate(to: string) {
  if (typeof window === "undefined") return;

  const hashIdx = to.indexOf("#");
  const targetHash = hashIdx !== -1 ? to.slice(hashIdx + 1) : "";
  const targetPath = hashIdx !== -1 ? to.slice(0, hashIdx) || window.location.pathname : to;

  const isSamePath = window.location.pathname === targetPath;

  if (targetHash && isSamePath) {
    window.history.pushState(null, "", `${targetPath}#${targetHash}`);
    window.dispatchEvent(new PopStateEvent("popstate"));
    scrollToElement(targetHash);
    return;
  }

  // Standard path navigation
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));

  if (targetHash) {
    setTimeout(() => {
      scrollToElement(targetHash);
    }, 100);
  }
}

export function parsePath(pathname: string, rawHash = ""): RouteState {
  const parts = (pathname || "/").split("/").filter(Boolean);
  const primaryPath = "/" + (parts[0] || "");
  const cleanHash = rawHash.startsWith("#") && !rawHash.startsWith("#/") ? rawHash.slice(1) : "";
  const subSection = parts.slice(1).join("/") || cleanHash;

  return {
    path: primaryPath,
    section: subSection,
    hash: cleanHash,
  };
}

export function useRoute(defaultPath = "/"): RouteState {
  const parse = (): RouteState => {
    if (typeof window === "undefined") {
      return parsePath(defaultPath);
    }

    // 1. Backward compatibility: if URL contains legacy #/page, migrate to clean /page
    if (window.location.hash.startsWith("#/")) {
      const legacyPath = window.location.hash.slice(1);
      window.history.replaceState(null, "", legacyPath);
    }

    const pathname = window.location.pathname || "/";
    const rawHash = window.location.hash || "";

    return parsePath(pathname, rawHash);
  };

  const [route, setRoute] = useState<RouteState>(parse);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = () => {
      setRoute(parse());
    };

    window.addEventListener("popstate", handler);
    window.addEventListener("hashchange", handler);

    // Global link interceptor: smoothly handle SPA navigation for internal links
    const handleGlobalClick = (e: MouseEvent) => {
      // Don't intercept if modifier keys are pressed (cmd/ctrl click for new tab)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.defaultPrevented) return;

      const target = (e.target as HTMLElement)?.closest("a");
      if (!target) return;

      const href = target.getAttribute("href");
      if (!href) return;

      // Ignore external links, mailto, tel, downloads, or target="_blank"
      if (
        target.target === "_blank" ||
        target.hasAttribute("download") ||
        href.startsWith("http://") ||
        href.startsWith("https://") ||
        href.startsWith("mailto:") ||
        href.startsWith("tel:")
      ) {
        return;
      }

      // Convert legacy #/ link clicks to clean path
      if (href.startsWith("#/")) {
        e.preventDefault();
        navigate(href.slice(1));
        return;
      }

      // Handle internal relative paths
      if (href.startsWith("/")) {
        e.preventDefault();
        navigate(href);
        return;
      }
    };

    document.addEventListener("click", handleGlobalClick);

    return () => {
      window.removeEventListener("popstate", handler);
      window.removeEventListener("hashchange", handler);
      document.removeEventListener("click", handleGlobalClick);
    };
  }, []);

  return route;
}
